import React, { useState } from 'react';
import {
  Box,
  Button,
  Grid,
  IconButton,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import {
  ArrowDownward,
  ArrowUpward,
  Cancel,
  Check,
  CheckCircle,
  Close,
  Delete,
  Edit,
  ReportProblem,
} from '@mui/icons-material';
import ContentPreviewCard from '../../components/ContentPreviewCard';
import HouseCollectionEmptyState from '../../components/HouseCollectionEmptyState';
import HouseCollectionGrid from '../../components/HouseCollectionGrid';
import HouseCollectionHeader from '../../components/HouseCollectionHeader';
import LinkifiedText from '../../components/LinkifiedText';
import { formatDate } from '../../utils/format';
import {
  isGuideType,
  isProblemType,
} from './config';
import {
  getSeverityChip,
  getStatusChip,
  getTypeIcon,
  Place,
  renderLocationLink,
} from './helpers';
import {
  HouseContent,
  HouseContentStatus,
  HouseContentType,
  PageText,
  TranslateFunction,
} from './types';

interface ContentPageHeaderProps {
  contentType: HouseContentType;
  pageText: PageText;
  canCreate: boolean;
  onCreate: () => void;
}

export const ContentPageHeader: React.FC<ContentPageHeaderProps> = ({
  contentType,
  pageText,
  canCreate,
  onCreate,
}) => (
  <HouseCollectionHeader
    title={pageText.title}
    description={pageText.intro}
    actionLabel={canCreate ? pageText.addAction : undefined}
    onAction={canCreate ? onCreate : undefined}
    titleVariant={contentType === 'problem' ? 'h4' : 'h3'}
    descriptionVariant="body1"
    marginBottom={3}
  />
);

interface ContentPageEmptyStateProps {
  pageText: PageText;
  canCreate: boolean;
  onCreate: () => void;
}

export const ContentPageEmptyState: React.FC<ContentPageEmptyStateProps> = ({
  pageText,
  canCreate,
  onCreate,
}) => (
  <HouseCollectionEmptyState
    title={pageText.emptyTitle}
    description={pageText.emptyDescription}
    actionLabel={canCreate ? pageText.emptyAction : undefined}
    onAction={canCreate ? onCreate : undefined}
  />
);

interface ContentCardsProps {
  contentType: HouseContentType;
  items: HouseContent[];
  pageText: PageText;
  isOwner: boolean;
  canReorderCategories: boolean;
  komootPreviewImages: Record<string, string>;
  language: string;
  t: TranslateFunction;
  canEditItem: (item: HouseContent) => boolean;
  onView: (item: HouseContent) => void;
  onEdit: (item: HouseContent) => void;
  onDelete: (itemId: string) => void;
  onReorderCategory: (category: string, direction: 'up' | 'down') => void;
  onRenameCategory?: (oldName: string, newName: string) => Promise<void>;
  onUpdateStatus: (itemId: string, status: HouseContentStatus, fallback: string) => void;
}

interface CategoryGroup {
  key: string;
  label: string;
  sortOrder: number;
  isUncategorized: boolean;
  items: HouseContent[];
}

const categoryRowSx = {
  flexWrap: 'nowrap',
  overflowX: 'auto',
  scrollSnapType: 'x mandatory',
  pb: 1,
  px: 0.5,
  mx: -0.5,
  scrollbarWidth: 'none',
  '&::-webkit-scrollbar': { display: 'none' },
} as const;

const normalizeCategoryLabel = (category?: string | null) => category?.trim() || '';

const buildCategoryGroups = (
  items: HouseContent[],
  t: TranslateFunction,
): CategoryGroup[] => {
  const categoryMap = new Map<string, CategoryGroup>();

  items.forEach((item) => {
    const normalizedLabel = normalizeCategoryLabel(item.category);
    const key = normalizedLabel ? normalizedLabel.toLocaleLowerCase() : '';

    if (!categoryMap.has(key)) {
      categoryMap.set(key, {
        key: key || '__uncategorized__',
        label: normalizedLabel || t('common.labels.uncategorized'),
        sortOrder: normalizedLabel
          ? (item.categoryOrder > 0 ? item.categoryOrder : Number.MAX_SAFE_INTEGER)
          : 0,
        isUncategorized: !normalizedLabel,
        items: [],
      });
    }

    const categoryGroup = categoryMap.get(key);

    if (categoryGroup && normalizedLabel) {
      categoryGroup.sortOrder = Math.min(
        categoryGroup.sortOrder,
        item.categoryOrder > 0 ? item.categoryOrder : Number.MAX_SAFE_INTEGER,
      );
    }

    categoryGroup?.items.push(item);
  });

  return Array.from(categoryMap.entries())
    .sort(([, leftGroup], [, rightGroup]) => {
      if (leftGroup.isUncategorized) {
        return -1;
      }

      if (rightGroup.isUncategorized) {
        return 1;
      }

      if (leftGroup.sortOrder !== rightGroup.sortOrder) {
        return leftGroup.sortOrder - rightGroup.sortOrder;
      }

      return leftGroup.label.localeCompare(rightGroup.label, undefined, { sensitivity: 'base' });
    })
    .map(([, group]) => group);
};

const buildMenuActions = (
  item: HouseContent,
  canEdit: boolean,
  onEdit: (item: HouseContent) => void,
  onDelete: (itemId: string) => void,
  t: TranslateFunction,
) => (
  canEdit ? [
    {
      label: t('common.actions.edit'),
      icon: <Edit fontSize="small" />,
      onClick: () => onEdit(item),
    },
    {
      label: t('common.actions.delete'),
      icon: <Delete fontSize="small" />,
      onClick: () => onDelete(item.id),
      destructive: true,
    },
  ] : undefined
);

export const ContentCards: React.FC<ContentCardsProps> = ({
  contentType,
  items,
  pageText,
  isOwner,
  canReorderCategories,
  komootPreviewImages,
  language,
  t,
  canEditItem,
  onView,
  onEdit,
  onDelete,
  onReorderCategory,
  onRenameCategory,
  onUpdateStatus,
}) => {
  const categoryGroups = buildCategoryGroups(items, t);
  const reorderableCategoryKeys = categoryGroups.filter((group) => !group.isUncategorized).map((group) => group.key);
  const [editingCategoryKey, setEditingCategoryKey] = useState<string | null>(null);
  const [editingValue, setEditingValue] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  const handleStartEdit = (key: string, label: string) => {
    setEditingCategoryKey(key);
    setEditingValue(label);
  };

  const handleCancelEdit = () => {
    setEditingCategoryKey(null);
    setEditingValue('');
  };

  const handleSaveEdit = async (oldName: string) => {
    if (!onRenameCategory || !editingValue.trim() || isSaving) {
      return;
    }

    setIsSaving(true);

    try {
      await onRenameCategory(oldName, editingValue.trim());
      setEditingCategoryKey(null);
      setEditingValue('');
    } finally {
      setIsSaving(false);
    }
  };

  if (isGuideType(contentType)) {
    return (
      <Stack spacing={3}>
        {categoryGroups.map((group) => (
          <Box key={group.key}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 1.25 }}>
              {editingCategoryKey === group.key ? (
                <Stack direction="row" spacing={0.5} alignItems="center" sx={{ flex: 1 }}>
                  <TextField
                    size="small"
                    value={editingValue}
                    autoFocus
                    disabled={isSaving}
                    onChange={(e) => setEditingValue(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') handleSaveEdit(group.label);
                      if (e.key === 'Escape') handleCancelEdit();
                    }}
                    sx={{ flex: 1 }}
                  />
                  <IconButton
                    size="small"
                    aria-label={t('common.actions.save')}
                    disabled={!editingValue.trim() || isSaving}
                    onClick={() => handleSaveEdit(group.label)}
                  >
                    <Check fontSize="small" />
                  </IconButton>
                  <IconButton
                    size="small"
                    aria-label={t('common.actions.cancel')}
                    disabled={isSaving}
                    onClick={handleCancelEdit}
                  >
                    <Close fontSize="small" />
                  </IconButton>
                </Stack>
              ) : (
                <>
                  <Typography variant="overline" color="text.secondary" sx={{ display: 'block' }}>
                    {group.label}
                  </Typography>
                  {canReorderCategories && !group.isUncategorized && (
                    <Stack direction="row" spacing={0.5}>
                      {onRenameCategory && (
                        <IconButton
                          size="small"
                          aria-label={t('common.actions.renameCategory')}
                          onClick={() => handleStartEdit(group.key, group.label)}
                        >
                          <Edit fontSize="small" />
                        </IconButton>
                      )}
                      <IconButton
                        size="small"
                        aria-label={t('common.actions.moveUp')}
                        disabled={reorderableCategoryKeys[0] === group.key}
                        onClick={() => onReorderCategory(group.label, 'up')}
                      >
                        <ArrowUpward fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        aria-label={t('common.actions.moveDown')}
                        disabled={reorderableCategoryKeys[reorderableCategoryKeys.length - 1] === group.key}
                        onClick={() => onReorderCategory(group.label, 'down')}
                      >
                        <ArrowDownward fontSize="small" />
                      </IconButton>
                    </Stack>
                  )}
                </>
              )}
            </Box>
            <HouseCollectionGrid horizontalOnMobile={false} spacing={2} sx={categoryRowSx}>
              {group.items.map((item) => {
                const canEdit = canEditItem(item);

                return (
                  <Grid item xs={8} sm={6} key={item.id} sx={{ flexShrink: 0, scrollSnapAlign: 'start' }}>
                    <ContentPreviewCard
                      menuAriaLabel={t('common.labels.menu')}
                      onClick={() => onView(item)}
                      menuActions={buildMenuActions(item, canEdit, onEdit, onDelete, t)}
                      media={
                        <Box
                          sx={{
                            position: 'relative',
                            minHeight: { xs: 210, sm: 240, md: 260 },
                            background: item.images?.[0]?.url
                              ? `linear-gradient(180deg, rgba(14,18,15,0.08) 0%, rgba(14,18,15,0.74) 100%), url(${item.images[0].url}) center/cover`
                              : 'linear-gradient(135deg, rgba(245,223,194,0.98) 0%, rgba(241,232,219,0.96) 60%, rgba(225,213,198,0.98) 100%)',
                          }}
                        >
                          {!item.images?.[0]?.url && (
                            <Box sx={{ position: 'absolute', inset: 0, display: 'grid', placeItems: 'center' }}>
                              {getTypeIcon(contentType)}
                            </Box>
                          )}
                          <Box
                            sx={{
                              position: 'absolute',
                              left: 0,
                              right: 0,
                              bottom: 0,
                              p: { xs: 1.5, sm: 2 },
                              color: item.images?.[0]?.url ? 'common.white' : 'text.primary',
                            }}
                          >
                            <Typography variant="h5" sx={{ color: 'inherit' }}>
                              {item.title}
                            </Typography>
                          </Box>
                        </Box>
                      }
                      contentSx={{ p: { xs: 1.5, sm: 2 } }}
                    >
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{
                          minHeight: { xs: 58, sm: 72 },
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          display: '-webkit-box',
                          WebkitLineClamp: 3,
                          WebkitBoxOrient: 'vertical',
                        }}
                      >
                        {item.description}
                      </Typography>
                    </ContentPreviewCard>
                  </Grid>
                );
              })}
            </HouseCollectionGrid>
          </Box>
        ))}
      </Stack>
    );
  }

  if (!isProblemType(contentType)) {
    return (
      <Stack spacing={3}>
        {categoryGroups.map((group) => (
          <Box key={group.key}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 1.25 }}>
              {editingCategoryKey === group.key ? (
                <Stack direction="row" spacing={0.5} alignItems="center" sx={{ flex: 1 }}>
                  <TextField
                    size="small"
                    value={editingValue}
                    autoFocus
                    disabled={isSaving}
                    onChange={(e) => setEditingValue(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') handleSaveEdit(group.label);
                      if (e.key === 'Escape') handleCancelEdit();
                    }}
                    sx={{ flex: 1 }}
                  />
                  <IconButton
                    size="small"
                    aria-label={t('common.actions.save')}
                    disabled={!editingValue.trim() || isSaving}
                    onClick={() => handleSaveEdit(group.label)}
                  >
                    <Check fontSize="small" />
                  </IconButton>
                  <IconButton
                    size="small"
                    aria-label={t('common.actions.cancel')}
                    disabled={isSaving}
                    onClick={handleCancelEdit}
                  >
                    <Close fontSize="small" />
                  </IconButton>
                </Stack>
              ) : (
                <>
                  <Typography variant="overline" color="text.secondary" sx={{ display: 'block' }}>
                    {group.label}
                  </Typography>
                  {canReorderCategories && !group.isUncategorized && (
                    <Stack direction="row" spacing={0.5}>
                      {onRenameCategory && (
                        <IconButton
                          size="small"
                          aria-label={t('common.actions.renameCategory')}
                          onClick={() => handleStartEdit(group.key, group.label)}
                        >
                          <Edit fontSize="small" />
                        </IconButton>
                      )}
                      <IconButton
                        size="small"
                        aria-label={t('common.actions.moveUp')}
                        disabled={reorderableCategoryKeys[0] === group.key}
                        onClick={() => onReorderCategory(group.label, 'up')}
                      >
                        <ArrowUpward fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        aria-label={t('common.actions.moveDown')}
                        disabled={reorderableCategoryKeys[reorderableCategoryKeys.length - 1] === group.key}
                        onClick={() => onReorderCategory(group.label, 'down')}
                      >
                        <ArrowDownward fontSize="small" />
                      </IconButton>
                    </Stack>
                  )}
                </>
              )}
            </Box>
            <HouseCollectionGrid horizontalOnMobile={false} spacing={2} sx={categoryRowSx}>
              {group.items.map((item) => {
                const canEdit = canEditItem(item);
                const previewImageUrl = item.images?.[0]?.url
                  || (contentType === 'activities' && item.komootUrl ? komootPreviewImages[item.komootUrl] : undefined);

                return (
                  <Grid item xs={8} sm={6} md={4} key={item.id} sx={{ flexShrink: 0, scrollSnapAlign: 'start' }}>
                    <ContentPreviewCard
                      menuAriaLabel={t('common.labels.menu')}
                      onClick={() => onView(item)}
                      menuActions={buildMenuActions(item, canEdit, onEdit, onDelete, t)}
                      media={
                        <Box
                          sx={{
                            position: 'relative',
                            minHeight: { xs: 220, sm: 250, md: 280 },
                            background: previewImageUrl
                              ? `linear-gradient(180deg, rgba(15,18,16,0.08) 10%, rgba(15,18,16,0.74) 100%), url(${previewImageUrl}) center/cover`
                              : 'linear-gradient(135deg, rgba(35,68,54,0.92) 0%, rgba(79,110,97,0.84) 52%, rgba(200,107,74,0.72) 100%)',
                          }}
                        >
                          {!previewImageUrl && (
                            <Box sx={{ position: 'absolute', inset: 0, display: 'grid', placeItems: 'center' }}>
                              {getTypeIcon(contentType)}
                            </Box>
                          )}
                          <Box sx={{ position: 'absolute', left: 0, right: 0, bottom: 0, p: { xs: 1.5, sm: 2 }, color: 'common.white' }}>
                            <Typography variant="h6" sx={{ color: 'common.white', mb: 0.5 }}>
                              {item.title}
                            </Typography>
                            {(item.location || item.address || (item.latitude != null && item.longitude != null)) && (
                              <Stack direction="row" spacing={0.75} alignItems="center" sx={{ color: 'rgba(255,250,245,0.84)' }}>
                                <Place fontSize="small" />
                                <Typography variant="body2" sx={{ color: 'inherit' }}>
                                  {renderLocationLink(item.location, item.latitude, item.longitude, true, item.address)}
                                </Typography>
                              </Stack>
                            )}
                          </Box>
                        </Box>
                      }
                      contentSx={{ p: { xs: 1.5, sm: 2 } }}
                      footer={(
                        <Box sx={{ px: { xs: 1.5, sm: 2 }, pb: { xs: 1.5, sm: 2 }, pt: 0 }}>
                          <Stack direction="row" spacing={1} justifyContent="space-between" alignItems="center">
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                              {contentType === 'activities' && item.komootUrl && (
                                <Button size="small" href={item.komootUrl} target="_blank" rel="noreferrer">
                                  {pageText.externalLinkLabel}
                                </Button>
                              )}
                            </Box>
                            {contentType === 'activities' && isOwner && item.status === 'pending' && (
                              <Stack direction="row" spacing={0.5}>
                                <IconButton
                                  size="small"
                                  color="success"
                                  onClick={() => onUpdateStatus(item.id, 'approved', t('activities.failedApprove'))}
                                >
                                  <CheckCircle fontSize="small" />
                                </IconButton>
                                <IconButton
                                  size="small"
                                  color="error"
                                  onClick={() => onUpdateStatus(item.id, 'rejected', t('activities.failedReject'))}
                                >
                                  <Cancel fontSize="small" />
                                </IconButton>
                              </Stack>
                            )}
                          </Stack>
                        </Box>
                      )}
                    >
                      <LinkifiedText
                        text={item.description}
                        variant="body2"
                        color="text.secondary"
                        clampLines={3}
                        stopLinkPropagation
                        sx={{ minHeight: { xs: 58, sm: 64 } }}
                      />
                    </ContentPreviewCard>
                  </Grid>
                );
              })}
            </HouseCollectionGrid>
          </Box>
        ))}
      </Stack>
    );
  }

  return (
    <Grid container spacing={2}>
      {items.map((item) => {
        const canEdit = canEditItem(item);

        return (
          <Grid item xs={12} sm={6} key={item.id}>
            <ContentPreviewCard
              menuAriaLabel={t('common.labels.menu')}
              onClick={() => onView(item)}
              menuActions={buildMenuActions(item, canEdit, onEdit, onDelete, t)}
              contentSx={{ p: { xs: 1.5, sm: 2 }, pr: canEdit ? 8 : { xs: 1.5, sm: 2 } }}
            >
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1, mb: 1 }}>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
                  <ReportProblem fontSize="small" color="primary" />
                  <Typography variant="h6" sx={{ minWidth: 0 }}>
                    {item.title}
                  </Typography>
                </Stack>
                <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                  {getSeverityChip(item.severity, t)}
                  {getStatusChip(item.status, t)}
                </Box>
              </Box>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{
                  minHeight: 58,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  display: '-webkit-box',
                  WebkitLineClamp: 3,
                  WebkitBoxOrient: 'vertical',
                }}
              >
                {item.description}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.25 }}>
                {t('common.labels.reportedOn', { date: formatDate(item.createdAt, language) })}
              </Typography>
              {item.images && item.images.length > 0 && (
                <Box
                  sx={{
                    mt: 1.5,
                    display: 'flex',
                    gap: 1,
                    overflowX: 'auto',
                    scrollSnapType: 'x mandatory',
                    pb: 0.5,
                    scrollbarWidth: 'none',
                    '&::-webkit-scrollbar': { display: 'none' },
                  }}
                >
                  {item.images.map((image) => (
                    <Box
                      key={image.url}
                      component="img"
                      src={image.url}
                      alt={image.originalName || item.title}
                      sx={{ width: 88, height: 66, objectFit: 'cover', borderRadius: 1.5, flexShrink: 0, scrollSnapAlign: 'start' }}
                    />
                  ))}
                </Box>
              )}
            </ContentPreviewCard>
          </Grid>
        );
      })}
    </Grid>
  );
};